/**
 * @namespace ThemeEvents
 * @description A collection of modal-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new ModalOpenEvent(modalId, data));
 * document.addEventListener(ThemeEvents.subscribeModalOpen, (e) => { console.log(e.detail.modalId) });
 */
export class ThemeEvents {
	/** @static @constant {string} Event triggered when a modal is opened */
	static subscribeModalOpen = "subscribe-modal:open";
	/** @static @constant {string} Event triggered when a modal is closed */
	static subscribeModalClose = "subscribe-modal:close";
	/** @static @const {string Event triggered when mouse hover on modal */
	static subscribeButtonHover = "subscribe-button:hover";
	/** @static @constant {string} Event triggered when a variant is selected */
	static variantSelected = "variant:selected";
	/** @static @constant {string} Event triggered when a variant is changed */
	static variantUpdate = "variant:update";
	/** @static @constant {string} Event triggered when the cart items or quantities are updated */
	static cartUpdate = "cart:update";
	/** @static @constant {string} Event triggered when a cart update fails */
	static cartError = "cart:error";
	// Event triggered when quantity-selector value is changed
	static quantitySelectorUpdate = "quantity-selector:update";
	/** @static @constant {string} Event triggered when changing collection filters */
	static FilterUpdate = "filter:update";
	/** @static @constant {string} Event triggered when product page is initialized */
	static productPageInit = "product:init";
}

/**
 * Event fired when product page is initialized
 * @extends {CustomEvent}
 */
export class ProductInitEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.productPageInit, { bubbles: true, detail: data });
	}
}

/**
 * Event fired when a variant is selected
 * @extends {CustomEvent}
 */
export class VariantSelectedEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.variantSelected, { bubbles: true, detail: data });
	}
}
/**
 * Event fired when a variant is changed
 * @extends {CustomEvent}
 */
export class VariantUpdateEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.variantUpdate, { bubbles: true, detail: data });
	}
}

/**
 * Event fired when quantity is updated
 * @extends {CustomEvent}
 */
export class QuantityUpdateEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.quantitySelectorUpdate, {
			bubbles: true,
			detail: data,
		});
	}
}

/**
 * Event fired when cart is updated
 * @extends {CustomEvent}
 */
export class CartUpdateEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.cartUpdate, { bubbles: true, detail: data });
	}
}

/**
 * Event fired when cart error occurs
 * @extends {CustomEvent}
 */
export class CartErrorEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.cartError, { bubbles: true, detail: data });
	}
}

/**
 * Event fired when a modal is opened
 * @extends {CustomEvent}
 * @param {string} modalId - The ID of the modal to open
 * @param {Object} [data] - Additional data for the modal
 * @param {string} [data.title] - The title of the modal
 * @param {string} [data.content] - The content of the modal
 * @param {string} [data.size] - The size of the modal
 * @param {boolean} [data.closable] - Whether the modal can be closed
 * @param {Object} [data.customData] - Any custom data for the modal
 */
export class ModalOpenEvent extends CustomEvent {
	constructor(modalId, data = {}) {
		const detailData = {
			modalId,
			data: {
				title: data.title || "",
				content: data.content || "",
				size: data.size || "md",
				closable: data.closable !== false,
				...data,
			},
		};

		super(ThemeEvents.subscribeModalOpen, {
			bubbles: true,
			detail: detailData,
		});
	}
}

/**
 * Event fired when a modal is closed
 * @extends {CustomEvent}
 * @param {string} [modalId] - The ID of the modal to close (null to close all)
 * @param {Object} [data] - Additional data
 * @param {string} [data.reason] - The reason for closing the modal
 */
export class ModalCloseEvent extends CustomEvent {
	constructor(modalId = null, data = {}) {
		const detailData = {
			modalId,
			data: {
				reason: data.reason || "programmatic",
				...data,
			},
		};

		super(ThemeEvents.subscribeModalClose, {
			bubbles: true,
			detail: detailData,
		});
	}
}

/**
 * Event fired when subscribe button is hovered
 * @extends {CustomEvent}
 * @param {string} [buttonId] - The ID of the button
 * @param {Object} [data] - Additional data
 * @param {string} [data.hover] - The hover state of the button
 */
export class ButtonHoverEvent extends CustomEvent {
	constructor(buttonId = null, data = {}) {
		const detailData = {
			buttonId,
			data: {
				hover: data.hover || "false",
				...data,
			},
		};

		super(ThemeEvents.subscribeButtonHover, {
			bubbles: true,
			detail: detailData,
		});
	}
}

/**
 * Event fired when filter is updated
 * @extends {CustomEvent}
 */
export class FilterUpdateEvent extends CustomEvent {
	constructor(data = {}) {
		super(ThemeEvents.FilterUpdate, { bubbles: true, detail: data });
	}
}

// Expose to window
if (typeof window !== "undefined") {
	window.ThemeEvents = ThemeEvents;
	window.ModalOpenEvent = ModalOpenEvent;
	window.ModalCloseEvent = ModalCloseEvent;
	window.ButtonHoverEvent = ButtonHoverEvent;
	window.ProductInitEvent = ProductInitEvent;
	window.VariantSelectedEvent = VariantSelectedEvent;
	window.VariantUpdateEvent = VariantUpdateEvent;
	window.QuantityUpdateEvent = QuantityUpdateEvent;
	window.CartUpdateEvent = CartUpdateEvent;
	window.CartErrorEvent = CartErrorEvent;
	window.FilterUpdateEvent = FilterUpdateEvent;
}
